import axios, {
  AxiosError,
  type InternalAxiosRequestConfig,
} from "axios";

import { endSession } from "@/store/session";
import { useTenantStore } from "@/store/tenant";

const baseURL =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "/";

export const api = axios.create({
  baseURL,
  timeout: 30_000,
  headers: { "Content-Type": "application/json" },
  // S-SEC-3 — send the httpOnly auth cookies with every request. The
  // browser attaches access_token (and the refresh cookie on the
  // path-scoped refresh call); we no longer build an Authorization
  // header from JS-readable storage.
  withCredentials: true,
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  // S-SEC-3 — no Authorization header is constructed here anymore. Auth
  // travels via the httpOnly access_token cookie (withCredentials above);
  // the API reads the header OR the cookie, preferring the header for
  // API-token clients.

  // S2 — super admin tenant switcher: include ?tenant_id= on every
  // request when a super admin has scoped themselves to a tenant. The
  // backend's get_current_tenant_id dep reads this query param.
  try {
    // Read the tenant store lazily at request time via getState() (no React
    // hook here) so the current scope is always picked up. Uses the ESM
    // import above — the previous CommonJS require() threw "require is not
    // defined" in the Vite/ESM production bundle, which the catch silently
    // swallowed, so ?tenant_id was never appended in prod. No import cycle:
    // store/tenant.ts imports only zustand.
    const state = useTenantStore.getState();
    if (state.isSuperAdmin && state.currentTenantId) {
      const url = new URL(config.url ?? "", "http://x");
      url.searchParams.set("tenant_id", state.currentTenantId);
      config.url = url.pathname + url.search;
    }
  } catch {
    /* tenant store may not be available in some early init paths */
  }
  return config;
});

// ---------------------------------------------------------------------------
// Silent refresh on 401
// ---------------------------------------------------------------------------
// Access tokens are short-TTL. Without this, the Live View page (which
// polls /stream every 5–30s) would boot the user to /login the moment
// their token expired mid-session. The refresh token lives in a
// path-scoped httpOnly cookie; this interceptor POSTs /auth/refresh,
// the API validates the cookie and sets a fresh access_token cookie,
// then we re-run the original request (the new cookie applies
// automatically).
//
// Concurrency: 5 tiles can 401 at the same second. ``inflight`` ensures
// we only call /refresh once; the others ``await`` the same promise and
// then retry their original request.
type RetriableConfig = InternalAxiosRequestConfig & { _retry?: boolean };

let inflight: Promise<boolean> | null = null;

async function refreshAccessToken(): Promise<boolean> {
  try {
    // Use raw axios — going through ``api`` would re-trigger this
    // interceptor on a refresh-side 401 and recurse. ``withCredentials``
    // sends the httpOnly refresh cookie; no token is read from JS.
    // `undefined`, NOT `{}`. An empty object is a PRESENT body that must
    // validate, and `refresh_token` was required — so this call 422'd every time
    // and a 401'd session could never self-recover. The backend schema is now
    // tolerant too (refresh_token is Optional), but sending no body at all is the
    // honest expression of "the token is in the cookie".
    await axios.post(
      `${baseURL}/api/v1/auth/refresh`,
      undefined,
      { withCredentials: true },
    );
    return true;
  } catch {
    return false;
  }
}

api.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const original = error.config as RetriableConfig | undefined;
    const status = error.response?.status;

    // 401 from the refresh endpoint itself = refresh token also dead.
    // 401 from /auth/login = wrong credentials, no refresh attempt.
    // Both bypass the refresh path and fall through to wipe.
    const url = original?.url ?? "";
    const isAuthEndpoint =
      url.includes("/api/v1/auth/refresh") ||
      url.includes("/api/v1/auth/login");

    if (status === 401 && original && !original._retry && !isAuthEndpoint) {
      original._retry = true;
      if (!inflight) {
        inflight = refreshAccessToken().finally(() => {
          inflight = null;
        });
      }
      const refreshed = await inflight;
      if (refreshed) {
        // Re-run — the freshly-set access_token cookie applies
        // automatically via withCredentials.
        return api(original);
      }
    }

    if (status === 401) {
      // No refresh token, refresh failed, or this was already a retry —
      // wipe credentials AND the persisted tenant scope so the next render
      // redirects to /login with nothing of this session left behind.
      endSession();
    }
    return Promise.reject(error);
  },
);
